import React from 'react';
import { Search, X, Filter } from 'lucide-react';
import { useAppContext } from '../store';

interface SearchBarProps {
  darkMode: boolean;
  onToggleMobileFilters?: () => void;
  activeFilterCount?: number;
}

const SearchBar: React.FC<SearchBarProps> = ({ darkMode, onToggleMobileFilters, activeFilterCount = 0 }) => {
  const { state, dispatch } = useAppContext();
  const { searchQuery } = state.searchAndFilter;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value });
  };

  const handleClear = () => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: '' });
    inputRef.current?.focus();
  };

  return (
    <div className="max-w-4xl mx-auto mb-6">
      <div className="flex gap-2">
        <div className={`flex-1 relative group`}>
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className={`h-5 w-5 transition-colors ${
              darkMode ? 'text-appines-white/45 group-focus-within:text-appines-emerald' : 'text-appines-petrol/45 group-focus-within:text-appines-emerald'
            }`} />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleChange}
            placeholder="Rechercher un acte NGAP (ex: AMK 8, genou, rachis...)"
            className={`w-full pl-12 pr-10 py-3.5 rounded-xl border transition-all duration-200 text-base ${
              darkMode
                ? 'bg-appines-petrol border-appines-white/20 text-appines-white placeholder:text-appines-white/45 focus:border-appines-emerald focus:ring-2 focus:ring-appines-emerald/25'
                : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black placeholder:text-appines-petrol/45 focus:border-appines-emerald focus:ring-2 focus:ring-appines-emerald/25'
            }`}
            aria-label="Rechercher un acte NGAP"
          />
          {searchQuery && (
            <button
              onClick={handleClear}
              className={`absolute inset-y-0 right-0 pr-4 flex items-center transition-colors ${
                darkMode ? 'text-appines-white/45 hover:text-appines-white' : 'text-appines-petrol/45 hover:text-appines-petrol'
              }`}
              aria-label="Effacer la recherche"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Mobile filter button */}
        {onToggleMobileFilters && (
          <button
            onClick={onToggleMobileFilters}
            className={`md:hidden flex items-center justify-center px-3 rounded-xl border transition-colors relative ${
              darkMode
                ? 'bg-appines-petrol border-appines-white/20 text-appines-white/65 hover:text-appines-white hover:border-appines-white/40'
                : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-petrol/65 hover:text-appines-petrol hover:border-appines-petrol/40'
            }`}
            aria-label={`Filtres${activeFilterCount > 0 ? ` (${activeFilterCount} actif${activeFilterCount > 1 ? 's' : ''})` : ''}`}
          >
            <Filter className="h-5 w-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-appines-emerald text-appines-petrol text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
